/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
import React, { useState, useEffect } from "react";
import { toast } from "../../components/ui/sonner";
import { Building2, Pencil, Plus, ArrowUp, ArrowDown, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { inp } from "./hrmsShared";

export const buildTree = (branches) => {
  const kids = {};
  branches.forEach((b) => { if (b.id !== "hq") (kids[b.parentId || "hq"] = kids[b.parentId || "hq"] || []).push(b); });
  Object.values(kids).forEach((arr) => arr.sort((a, b) => (a.order - b.order) || a.zh.localeCompare(b.zh)));
  return kids;
};

export const rollup = (b, kids, key) => (kids[b.id] || []).reduce((s, k) => s + rollup(k, kids, key), b[key] || 0);

export const descendants = (id, kids) => (kids[id] || []).flatMap((k) => [k.id, ...descendants(k.id, kids)]);

const ownStats = (b, alerts) => ({
  actual: b.total || 0, approved: b.approvedTotal || 0,
  under: alerts.filter((a) => a.branch === b.id && a.type === "under").reduce((s, a) => s + (a.approved - a.actual), 0),
  over: alerts.filter((a) => a.branch === b.id && a.type === "over").reduce((s, a) => s + (a.actual - a.approved), 0),
});
const addStats = (x, y) => ({ actual: x.actual + y.actual, approved: x.approved + y.approved, under: x.under + y.under, over: x.over + y.over });
export const nodeStats = (b, kids, alerts) => {
  const own = ownStats(b, alerts);
  const all = (kids[b.id] || []).reduce((acc, k) => addStats(acc, nodeStats(k, kids, alerts).all), own);
  return { own, all };
};

export const levelSummary = (root, kids, alerts) => {
  const rows = [];
  const walk = (b, depth) => {
    const s = ownStats(b, alerts);
    rows[depth] = rows[depth] ? { ...addStats(rows[depth], s), depth, nodes: rows[depth].nodes + 1, names: [...rows[depth].names, b.zh] } : { ...s, depth, nodes: 1, names: [b.zh] };
    (kids[b.id] || []).forEach((k) => walk(k, depth + 1));
  };
  walk(root, 0);
  return rows;
};

const GapLine = ({ s, zh, incl }) => {
  if (s.under > 0 || s.over > 0) return (
    <p className="text-[11px] font-semibold truncate">
      {s.under > 0 && <span className="text-red-500">{zh ? `缺编 ${s.under}` : `short ${s.under}`}</span>}
      {s.under > 0 && s.over > 0 && <span className="text-slate-300"> · </span>}
      {s.over > 0 && <span className="text-amber-600">{zh ? `超编 ${s.over}` : `over ${s.over}`}</span>}
      {incl && <span className="text-slate-400 font-normal">{zh ? "（含下级）" : " (incl. sub)"}</span>}
    </p>
  );
  return s.approved > 0 ? <p className="text-[11px] text-emerald-600">{zh ? "满编" : "fully staffed"}</p> : null;
};

const EntityCard = ({ b, kids, alerts, isRoot, editing, canFullTree, zh, onClick, onEdit, onAdd, onMove, onDelete, siblings }) => {
  const { own, all } = nodeStats(b, kids, alerts);
  const hasKids = (kids[b.id] || []).length > 0;
  // 一-10「组织页人数不对（Employee headcount is incorrect）」修复（2026-10-15）：
  //   修改原因：卡片头部原用 all（本级 + 下级合计）显示人数，而法人切换 chip 原用本级、LevelSummary
  //     汇总也按各法人本级逐层相加 —— 同页三个口径互不一致。表现为：总部卡片 118 人、5 个分公司
  //     chip 相加 187 人，而全公司实际在职只有 118 人（下级人数被总部重复计了一次）。
  //   最终效果：卡片头部改为「本级」口径（own），与本页 chip、LevelSummary 汇总、员工列表总数
  //     （118）三处对齐；有下级时在次行单独标注「含下级 X/Y」，需要看集团合计时不会被漏掉。
  //   关键取舍：选择「本级」而不是「含下级」作为主口径 —— 因为法人是并列的法律实体，
  //     同一批下级员工不应同时算进上级法人的头部数字（否则各法人人数相加必然大于公司总人数）。
  // 预警红条：仍按「含下级」判定（下级有缺编/超编也要在上级卡片上提示），但主数字用本级。
  const warn = !isRoot && (all.under > 0 || (b.note || "").includes("筹建"));
  const pct = own.approved > 0 ? Math.min(100, Math.round((own.actual / own.approved) * 100)) : 100;
  const idx = siblings.findIndex((s) => s.id === b.id);
  const label = isRoot ? `${zh ? "集团总部" : "Group HQ"} · ${b.legalName || b.zh}` : (zh ? b.zh : (b.en || b.zh));
  return (
    <div data-testid={`hrms-tree-node-${isRoot ? "root" : b.id}`} data-actual={own.actual} data-approved={own.approved} data-under={own.under} data-over={own.over}
      data-all-actual={all.actual} data-all-approved={all.approved}
      className={`relative rounded-xl border bg-white px-4 py-3 w-[220px] hover:shadow-md transition-shadow ${warn ? "border-red-300" : isRoot ? "border-indigo-200" : "border-slate-200"}`}>
      {warn && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-red-500" />}
      <button data-testid={`hrms-tree-open-${isRoot ? "root" : b.id}`} onClick={onClick} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-left w-full">
        <div className="flex items-center gap-1.5">
          {isRoot && <Building2 className="h-4 w-4 text-indigo-600 shrink-0" />}
          <span className="font-bold text-slate-800 text-sm truncate" title={label}>{label}</span>
        </div>
        {!isRoot && b.legalName && <p className="text-[10px] text-slate-400 truncate" title={b.legalName}>{b.legalName}</p>}
        <p className="text-xs mt-1" data-testid={`hrms-tree-hc-${isRoot ? "root" : b.id}`}>
          <b className={warn ? "text-red-600" : "text-slate-800"}>{own.actual}</b>
          <span className="text-slate-500">/{own.approved}</span> <span className="text-slate-400">{zh ? "人（实际/编制）" : "actual/approved"}</span>
        </p>
        {hasKids && (
          <p className="text-[10px] text-slate-400 truncate" data-testid={`hrms-tree-own-${isRoot ? "root" : b.id}`}>
            {zh ? `含下级 ${all.actual}/${all.approved}（下级 ${all.actual - own.actual} 人）` : `incl. sub ${all.actual}/${all.approved} (sub ${all.actual - own.actual})`}
          </p>
        )}
        {b.leader && <p className="text-[10px] text-slate-400 truncate">{b.leader}{b.note ? ` · ${b.note}` : ""}</p>}
        {!b.leader && b.note && <p className="text-[10px] text-slate-400 truncate">{b.note}</p>}
        {/* 缺口行与头部同一口径（本级），避免再次出现「头部 49 人、缺口却按 118 人算」的错位；
            含下级的合计已在上一行单独标注，集团层面口径统一由下方 LevelSummary 汇总表呈现。 */}
        <GapLine s={own} zh={zh} incl={false} />
        <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden">
          <div className={`h-full rounded-full ${warn ? "bg-red-500" : "bg-indigo-600"}`} style={{ width: `${pct}%` }} />
        </div>
      </button>
      {editing && (
        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100" data-testid={`hrms-tree-actions-${isRoot ? "root" : b.id}`}>
          {/* 「编辑」：改本法人信息（名称/负责人/周报推送账号）—— admin / CEO / HR 均可（限本公司）。 */}
          <IconBtn tid={`hrms-tree-edit-${b.id}`} title={zh ? "编辑" : "Edit"} onClick={() => onEdit(b)}><Pencil className="h-3 w-3" /></IconBtn>
          {/* 新增下级 / 上移 / 下移 / 删除：属法人树结构变更，后端 _global_only + ORG_FULL 仅放行
              admin / CEO。2026-10-14 修复：原先只按 editing 渲染 → HR 看到按钮但点了必 403（假按钮）。 */}
          {canFullTree && <>
            <IconBtn tid={`hrms-tree-add-child-${b.id}`} title={zh ? "添加下级" : "Add child"} onClick={() => onAdd(b)}><Plus className="h-3 w-3" /></IconBtn>
            {!isRoot && <>
              <IconBtn tid={`hrms-tree-up-${b.id}`} title={zh ? "上移" : "Move up"} disabled={idx <= 0} onClick={() => onMove(b, -1)}><ArrowUp className="h-3 w-3" /></IconBtn>
              <IconBtn tid={`hrms-tree-down-${b.id}`} title={zh ? "下移" : "Move down"} disabled={idx >= siblings.length - 1} onClick={() => onMove(b, 1)}><ArrowDown className="h-3 w-3" /></IconBtn>
              <IconBtn tid={`hrms-tree-del-${b.id}`} title={zh ? "删除" : "Delete"} danger onClick={() => onDelete(b)}><Trash2 className="h-3 w-3" /></IconBtn>
            </>}
          </>}
        </div>
      )}
    </div>
  );
};

const IconBtn = ({ tid, title, onClick, disabled, danger, children }) => (
  <button type="button" data-testid={tid} title={title} disabled={disabled} onClick={onClick}
    className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-6 w-6 inline-flex items-center justify-center rounded border ${danger ? "border-red-200 text-red-500 hover:bg-red-50" : "border-slate-200 text-slate-500 hover:bg-slate-50"} disabled:opacity-30`}>{children}</button>
);

const Branch = ({ b, kids, isRoot, siblings, ...rest }) => {
  const children = kids[b.id] || [];
  return (
    <div className="flex flex-col items-center">
      <EntityCard b={b} kids={kids} isRoot={isRoot} siblings={siblings} {...rest} />
      {children.length > 0 && (
        <>
          <div className="h-4 w-px bg-slate-300" />
          <div className="relative flex gap-3 items-start pt-4">
            {children.length > 1 && <div className="absolute top-0 left-[110px] right-[110px] h-px bg-slate-300" />}
            {children.map((k) => (
              <div key={k.id} className="relative flex flex-col items-center">
                <div className="absolute -top-4 h-4 w-px bg-slate-300" />
                {React.createElement(Branch, { b: k, kids, siblings: children, ...rest })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const EntityTreeView = ({ root, kids, ...rest }) => (
  <div className="overflow-x-auto pb-2" data-testid="hrms-entity-tree">
    <div className="min-w-max flex justify-center px-2">
      <Branch b={root} kids={kids} isRoot siblings={[root]} {...rest} />
    </div>
  </div>
);

const LEVEL_ZH = ["集团总部", "分公司 / 法人", "子公司 / 事业部", "三级单位"];
const LEVEL_EN = ["Group HQ", "Branches", "Subsidiaries", "Level 3"];

export const LevelSummary = ({ root, kids, alerts, zh }) => {
  const rows = levelSummary(root, kids, alerts);
  const total = rows.reduce((acc, r) => addStats(acc, r), { actual: 0, approved: 0, under: 0, over: 0 });
  const T = (a, b) => (zh ? a : b);
  const lvl = (d) => (zh ? LEVEL_ZH[d] : LEVEL_EN[d]) || T(`第 ${d} 级`, `Level ${d}`);
  return (
    <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/60 overflow-hidden" data-testid="hrms-level-summary">
      <ResponsiveTable mobile="scroll" className="w-full text-[11px]">
        <thead>
          <tr className="text-slate-400 text-left">
            <th className="px-3 py-1.5 font-semibold">{T("层级", "Level")}</th>
            <th className="px-3 py-1.5 font-semibold">{T("法人数", "Entities")}</th>
            <th className="px-3 py-1.5 font-semibold">{T("实际 / 编制", "Actual / Approved")}</th>
            <th className="px-3 py-1.5 font-semibold">{T("缺编", "Short")}</th>
            <th className="px-3 py-1.5 font-semibold">{T("超编", "Over")}</th>
            <th className="px-3 py-1.5 font-semibold">{T("到位率", "Fill")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.depth} className="border-t border-slate-100" data-testid={`hrms-level-row-${r.depth}`} data-under={r.under} data-over={r.over}>
              <td className="px-3 py-1.5 font-semibold text-slate-700" title={r.names.join("、")}>{lvl(r.depth)}</td>
              <td className="px-3 py-1.5 text-slate-600">{r.nodes}</td>
              <td className="px-3 py-1.5 text-slate-700"><b>{r.actual}</b> / {r.approved}</td>
              <td className={`px-3 py-1.5 font-semibold ${r.under > 0 ? "text-red-500" : "text-slate-300"}`}>{r.under || "—"}</td>
              <td className={`px-3 py-1.5 font-semibold ${r.over > 0 ? "text-amber-600" : "text-slate-300"}`}>{r.over || "—"}</td>
              <td className="px-3 py-1.5 text-slate-600">{r.approved > 0 ? `${Math.min(100, Math.round((r.actual / r.approved) * 100))}%` : "—"}</td>
            </tr>
          ))}
          <tr className="border-t border-slate-200 bg-white" data-testid="hrms-level-row-total">
            <td className="px-3 py-1.5 font-bold text-slate-800">{T("集团合并", "Group total")}</td>
            <td className="px-3 py-1.5 text-slate-600">{rows.reduce((s, r) => s + r.nodes, 0)}</td>
            <td className="px-3 py-1.5 text-slate-800"><b>{total.actual}</b> / {total.approved}</td>
            <td className={`px-3 py-1.5 font-bold ${total.under > 0 ? "text-red-500" : "text-slate-300"}`}>{total.under || "—"}</td>
            <td className={`px-3 py-1.5 font-bold ${total.over > 0 ? "text-amber-600" : "text-slate-300"}`}>{total.over || "—"}</td>
            <td className="px-3 py-1.5 text-slate-700">{total.approved > 0 ? `${Math.min(100, Math.round((total.actual / total.approved) * 100))}%` : "—"}</td>
          </tr>
        </tbody>
      </ResponsiveTable>
    </div>
  );
};

export const EntityDialog = ({ api, dlg, branches, kids, zh, onClose, onSaved }) => {
  const T = (z, e) => (zh ? z : e);
  const [f, setF] = useState(dlg);
  const [busy, setBusy] = useState(false);
  const [dir, setDir] = useState([]);
  useEffect(() => { api.get("/users/directory").then((r) => setDir((r.data || []).filter((u) => !["subcontractor", "owner", "vendor"].includes(u.role)).sort((a, b) => (a.name || a.username).localeCompare(b.name || b.username)))).catch(() => {}); }, [api]);
  const isNew = !f.id;
  const blocked = new Set(f.id ? [f.id, ...descendants(f.id, kids)] : []);
  const parents = branches.filter((b) => !blocked.has(b.id));
  const set = (k) => (e) => setF((d) => ({ ...d, [k]: e.target.value }));
  const save = async () => {
    if (!f.zh.trim()) return toast.error(T("中文名称必填", "Chinese name required"));
    setBusy(true);
    try {
      const body = { zh: f.zh, en: f.en, legalName: f.legalName, leader: f.leader, leaderUsername: f.leaderUsername || "", digestCcEng: !!f.digestCcEng, note: f.note, ...(f.id === "hq" ? {} : { parentId: f.parentId || "hq" }) };
      if (isNew) await api.post("/hrms/entities", body); else await api.put(`/hrms/entities/${f.id}`, body);
      toast.success(isNew ? T("法人已新增", "Entity added") : T("法人信息已更新", "Entity updated"));
      onSaved();
    } catch (e) { toast.error(e?.detailText || T("保存失败", "Save failed")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="hrms-entity-dialog">
        <DialogHeader><DialogTitle>{isNew ? T("新增法人 / 分公司", "New Entity / Branch") : `${T("编辑法人信息", "Edit Entity")} · ${dlg.zh}`}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">{T("中文名称 *", "Chinese name *")}<input data-testid="hrms-entity-zh" className={inp} value={f.zh} onChange={set("zh")} placeholder={T("如 圣地亚哥", "e.g. 圣地亚哥")} /></label>
            <label className="block">{T("英文名称", "English name")}<input data-testid="hrms-entity-en" className={inp} value={f.en} onChange={set("en")} placeholder="e.g. San Diego" /></label>
          </div>
          <label className="block">{T("法定名称（营业执照）", "Legal name")}<input data-testid="hrms-entity-legal" className={inp} value={f.legalName} onChange={set("legalName")} placeholder={T("如 Aligcus SD LLC", "e.g. Aligcus SD LLC")} /></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">{T("负责人", "Leader")}<input data-testid="hrms-meta-leader" className={inp} value={f.leader} onChange={set("leader")} placeholder={T("如 Torny Li", "e.g. Torny Li")} /></label>
            <label className="block">{T("备注", "Note")}<input data-testid="hrms-meta-note" className={inp} value={f.note} onChange={set("note")} placeholder={T("如 筹建中 · 负责人待招募", "e.g. In formation")} /></label>
          </div>
          {/* 三-2「Not sure what the Leader Account section does」（HR 反馈：不知道 Leader Account 这一栏是干什么的）：
              修改原因：原先这里只是一个孤立的下拉 + 一行小字说明，HR 看不出它的用途、也不知道与上面「负责人」的区别。
              最终效果：改成一个带标题的独立区块「周报推送设置（Leader Account）」，并在区块内用三条说明讲清——
                ① 作用（只决定谁收到该法人每周一的经营周报：站内铃铛 + 邮件）；
                ② 不设置时的匹配规则（按「负责人」姓名自动匹配，匹配不到兜底工程经理）；
                ③ 与「负责人」的区别（负责人只是名片名称，真正收周报的是这里选的账号），且不涉及任何系统权限。
              关键取舍：只做说明与分组，不新增字段 —— 功能本身已齐备，问题是"看不懂"，加说明比改逻辑更贴合诉求。 */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-2" data-testid="hrms-entity-digest-block">
            <p className="text-xs font-semibold text-slate-700">{T("周报推送设置（Leader Account）", "Weekly digest setting (Leader Account)")}</p>
            <label className="block text-xs text-slate-600">{T("推送账号", "Recipient account")}
              <select data-testid="hrms-meta-leader-user" className={inp} value={f.leaderUsername || ""} onChange={set("leaderUsername")}>
                <option value="">{T("未设置 — 按上方「负责人」姓名自动匹配；匹配不到则兜底发给工程经理", "Not set — matched by the Leader name above; falls back to construction managers if no match")}</option>
                {dir.map((u) => <option key={u.username} value={u.username}>{u.name || u.username}{u.email ? ` · ${u.email}` : ""}</option>)}
              </select>
            </label>
            <ul className="text-[11px] text-slate-500 list-disc pl-4 space-y-0.5">
              <li>{T("作用：只决定「谁收到该法人每周一的经营周报」（站内铃铛 + 邮件），不涉及任何系统权限。", "What it does: only decides who receives this entity's Monday operating digest (in-app bell + email). It grants no system permission.")}</li>
              <li>{T("不设置时：先按上方「负责人」姓名自动匹配账号；匹配不到则兜底发给工程经理。", "If unset: matched automatically by the Leader name above; if no match, it falls back to construction managers.")}</li>
              <li>{T("与「负责人」的区别：负责人只是对外展示的名称，真正收到周报的是这里指定的账号。", "Difference from Leader: Leader is only the display name — the digest actually goes to the account chosen here.")}</li>
            </ul>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="hrms-meta-digest-cc" checked={!!f.digestCcEng} onChange={(e) => setF((d) => ({ ...d, digestCcEng: e.target.checked }))} />
              {T("周报同时抄送全部工程经理", "Also CC all construction managers on the weekly digest")}
            </label>
          </div>
          {f.id !== "hq" && (
            <label className="block">{T("上级法人", "Parent entity")}
              <select data-testid="hrms-entity-parent" className={inp} value={f.parentId || "hq"} onChange={set("parentId")}>
                {parents.map((b) => <option key={b.id} value={b.id}>{b.id === "hq" ? `${T("集团总部", "Group HQ")} · ${b.legalName || b.zh}` : (zh ? b.zh : b.en || b.zh)}</option>)}
              </select>
            </label>
          )}
          {!isNew && <p className="text-[11px] text-slate-400">{T(`系统编号 ${f.id} 保持不变，改名不影响历史数据。`, `System id ${f.id} is kept; renaming does not affect history.`)}</p>}
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
            <button type="button" data-testid="hrms-entity-cancel" onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
            <button type="button" data-testid="hrms-meta-save" disabled={busy} onClick={save} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 inline-flex items-center gap-1 disabled:opacity-50">{busy && <Loader2 className="h-3 w-3 animate-spin" />}{T("保存", "Save")}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EntityDeleteDialog = ({ api, target, zh, onClose, onDeleted }) => {
  const T = (z, e) => (zh ? z : e);
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/hrms/entities/${target.id}`);
      toast.success(T("法人已删除", "Entity deleted")); onDeleted();
    } catch (e) { toast.error(e?.detailText || T("删除失败", "Delete failed")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="hrms-entity-del-dialog">
        <DialogHeader><DialogTitle>{T("删除法人", "Delete entity")} · {target.zh}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{T("仅当该法人下没有登录账号、在编员工、客户线索、分包/供应商及下级法人时才可删除；否则请先迁移。", "Deletion is only allowed when no accounts, employees, leads, vendors or child entities remain under it.")}</p>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
          <button type="button" data-testid="hrms-entity-del-cancel" onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
          <button type="button" data-testid="hrms-entity-del-confirm" disabled={busy} onClick={del} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 disabled:opacity-50">{T("确认删除", "Delete")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
